import { DateTime } from 'luxon'
import { useNavigate } from 'react-router'
import { Breadcrumb } from '../../components/breadcrumb.tsx'
import { FixFindingDrawer } from '../../components/drawers/fix-finding-drawer.tsx'
import { ScaFindingDrawer } from '../../components/drawers/sca-finding-drawer.tsx'
import { TriageFindingDrawer } from '../../components/drawers/triage-finding-drawer.tsx'
import { ErrorBoundary } from '../../components/error-boundaries/error-boundary'
import { NewerScanBanner } from '../../components/newer-scan-banner/newer-scan-banner.tsx'
import { ScanDetailsHeader } from '../../components/scan-details-header.tsx'
import { ScanFindingsTableControls } from '../../components/scan-findings-table-controls.tsx'
import { FilterStateStoreProvider } from '../../components/tables/filter-state-store.tsx'
import { getFindingQuickViewConfig, type FindingQuickViewId } from '../../components/tables/finding-quick-view-config'
import { ScanFindingsTable } from '../../components/tables/scan-findings-table.tsx'
import { Routes } from '../../routes.ts'
import { useRedirectFromOldDrawerQueryParams } from '../../utils/hooks/use-redirect-from-old-drawer-query-params.ts'
import { getToolName } from '../../utils/repository-utils.tsx'
import {
  useFindingQuickViewState,
  useGetScanAndRepository,
  useGetScanLifecycleState,
  useScanDetailsPageParams,
  useSwitchToAnalyzedOnFirstBatchComplete,
} from './scan-details-page-hooks.ts'
import * as styles from './scan-details-page.css.ts'

export const ScanDetailsPageProviders: React.FC<
  React.PropsWithChildren<{ quickViewId: FindingQuickViewId; detector?: string }>
> = ({ children, quickViewId, detector }) => {
  const quickViewConfig = getFindingQuickViewConfig(quickViewId)
  const storageKey = detector ? `filter-state/${detector}/${quickViewId}` : undefined

  return (
    <FilterStateStoreProvider
      key={quickViewId}
      initialState={quickViewConfig.defaultFilterState}
      storageKey={storageKey}
    >
      {children}
    </FilterStateStoreProvider>
  )
}

export const ScanDetailsPageWithProviders: React.FC = () => {
  const { scanId } = useScanDetailsPageParams()
  const { scan } = useGetScanAndRepository({ scanId })
  const { selectedQuickView } = useFindingQuickViewState({ scanId })
  const { scanLifecycleState } = useGetScanLifecycleState({ scanId })
  useSwitchToAnalyzedOnFirstBatchComplete({ scanId })

  const isInitialOrFirstBatch =
    scanLifecycleState?.type === 'initial-unanalyzed-scan' ||
    scanLifecycleState?.type === 'first-analysis-batch-in-progress'
  const effectiveQuickView = isInitialOrFirstBatch ? ('all-findings' as const) : selectedQuickView

  return (
    <ScanDetailsPageProviders quickViewId={effectiveQuickView} detector={scan?.detector}>
      <ScanDetailsPage scanId={scanId} />
    </ScanDetailsPageProviders>
  )
}

export const ScanDetailsPage: React.FC<{ scanId: string }> = ({ scanId }) => {
  useRedirectFromOldDrawerQueryParams()

  const { repository } = useGetScanAndRepository({ scanId })
  const deleteSuccessRedirectPath = repository?.id
    ? Routes.RepositoryDetailsPage.createPath({ repositoryId: repository.id, tab: 'scans' })
    : undefined

  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
      <div className={styles.pageContainer}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '100%' }}>
          <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <ScanDetailsPageBreadcrumb scanId={scanId} />
            <ScanDetailsNewerScanBanner scanId={scanId} />
          </div>
          <div style={{ marginLeft: '16px', marginRight: '16px', marginTop: '8px' }}>
            <ErrorBoundary fallbackTitle="Unable to load scan header">
              <ScanDetailsHeader scanId={scanId} deleteSuccessRedirectPath={deleteSuccessRedirectPath} />
            </ErrorBoundary>
          </div>
          <div style={{ marginTop: '8px' }}>
            <ErrorBoundary fallbackTitle="Unable to load controls">
              <ScanFindingsTableControls scanId={scanId} />
            </ErrorBoundary>
          </div>
          <div style={{ marginTop: '8px' }}>
            <ErrorBoundary fallbackTitle="Unable to load findings">
              <ScanFindingsTable scanId={scanId} />
            </ErrorBoundary>
          </div>
        </div>
      </div>
      <TriageFindingDrawer scanId={scanId} />
      <ScaFindingDrawer scanId={scanId} />
      <FixFindingDrawer scanId={scanId} />
    </div>
  )
}

const ScanDetailsNewerScanBanner: React.FC<{ scanId: string }> = ({ scanId }) => {
  const { scan, repository } = useGetScanAndRepository({ scanId })
  const navigate = useNavigate()

  if (!scan?.detector || !repository?.id) return null

  return (
    <div style={{ marginLeft: 'auto' }}>
      <NewerScanBanner
        currentScanId={scanId}
        repositoryId={repository.id}
        tool={scan.detector}
        // A newer scan of another branch is not a newer version of this one, as in the header beside it.
        branch={scan.branch ?? undefined}
        onView={({ newerScanId }) => navigate(Routes.ScanDetailsPage.createPath({ scanId: newerScanId }))}
      />
    </div>
  )
}

const ScanDetailsPageBreadcrumb: React.FC<{ scanId: string }> = ({ scanId }) => {
  const { scan, repository } = useGetScanAndRepository({ scanId })

  return (
    <ScanDetailsPageBreadcrumbPure
      repositoryId={repository?.id}
      repositoryName={repository?.name}
      branch={scan?.branch}
      toolName={getToolName(scan?.detector)}
      scannedAt={scan?.scanned_at}
    />
  )
}

export type ScanDetailsPageBreadcrumbPureProps = {
  repositoryId?: string
  repositoryName?: string
  branch?: string | null
  toolName?: string
  scannedAt?: string | null
}

export const ScanDetailsPageBreadcrumbPure: React.FC<ScanDetailsPageBreadcrumbPureProps> = ({
  repositoryId,
  repositoryName,
  branch,
  toolName,
  scannedAt,
}) => {
  const repoLabel = repositoryName && branch && toolName ? `${repositoryName} (${branch})(${toolName})` : undefined

  const formattedDate = scannedAt ? DateTime.fromISO(scannedAt).toFormat('MMMM d yyyy hh:mm:ssa ZZZZ') : undefined

  return (
    <Breadcrumb
      items={[
        { label: 'All repositories', to: Routes.RepositoriesPage.path },
        {
          label: repoLabel ?? 'Scan Details',
          to: repositoryId ? Routes.RepositoryDetailsPage.createPath({ repositoryId }) : '#',
        },
        {
          label: 'Scans',
          to: repositoryId ? Routes.RepositoryDetailsPage.createPath({ repositoryId, tab: 'scans' }) : '#',
        },
        {
          label: formattedDate ?? 'Scan Details',
          to: '#',
        },
      ]}
    />
  )
}
